

import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AppErrorMessageEvent, AppInfoMessageEvent, AppState, AppSuccessMessageEvent } from '..';
import { AppConfig } from '../../../environments/environment';
import { TableViewQueryCriteria, TableViewQueryResponse, TableViewSort } from '../../shared/components/table-view/table-view-component-base';

export type FindRowsRequest = {
    alternateViewName?: string,
    rowCriteria?: {[entitryAttrName:string]:any}[], // array of objects where each object has column key/value pairs (AND'd in SQL at server). If more than one obj, results are OR'd at server
    respEntityFields?: string[]; // list of TypeORM entity fields to return (not db col names)
    sorts?: TableViewSort[];
};

export class RestClientResult<ModelType> {
    data?: ModelType
    status?: { msg?: string, code?: number}
}

export class RestClient<ModelType> {

    urlBasePath: string
    urlPath: string

    idProperty: string = "id" // most db tables use 'id' for primary key

    constructor(
        public http: HttpClient,
        public subPath: string,
        public appState: AppState
    ) {
        this.urlBasePath = `${AppConfig.apiUrl}`
        this.urlPath = `${this.urlBasePath}/${this.subPath}`
    }

    getById = async ( id: string | number ) => {
        try {
            const result: any = await firstValueFrom(
              this.http.get(`${this.urlPath}/getById/${id}`)
            );
            const resp: ModelType = result.data;
            return resp;
          } catch (error) {
            console.error("getById() error: " + this.extractErrorMsg(error));
            throw error;
          }
    }

    getByIds = async ( ids: (string | number)[] ) => {
        try {
            const result: any = await firstValueFrom(
              this.http.post(`${this.urlPath}/getByIds`, { ids })
            );
            const resp: ModelType[] = result.data;
            return resp;
          } catch (error) {
            console.error("getByIds() error: " + this.extractErrorMsg(error));
            throw error;
          }
    }

    findAll = async () => await this.findRows();

    // simple query (server does AND on each attribute in obj and OR for each obj in array)
    // use args.respFields to limit data to specific entity fields
    findRows = async( args: FindRowsRequest = { rowCriteria: [{}] }) => {
        if ( !args.rowCriteria ) {
            args.rowCriteria = [{}];
        }
        args.rowCriteria?.forEach(rc=>{
            // Tell server to not include "deleted" records if not explicitly set
            if ( rc['deleted'] === undefined ) {
                rc.deleted = false;
            }
        });
        const { sorts, rowCriteria, ...baseParams} = args;
        const params = { 
            ...baseParams, 
        };
        if ( sorts ) {
            params['sorts'] = JSON.stringify(args.sorts);
        }
        if ( rowCriteria ) {
            params['rowCriteria'] = rowCriteria.map(rc=>JSON.stringify(rc))
        }
        try {
            const httpResp = await this.http.get<RestClientResult<Partial<ModelType>[]>>(
                `${this.urlPath}/findRows`, { params, observe: 'response' }).toPromise()
              const result = httpResp.body
              return result.data            
          } catch (error) {
            console.error("findRows() error: " + this.extractErrorMsg(error));
            throw error;
          }
    }

    findByAttrEq = async (attrName:string, attrValue:string) => this.findByAttrIn(attrName,[attrValue]);

    findByAttrIn = async (attrName: string, attrValues:string[]) => {
        const resp = await firstValueFrom(this.http.post(`${this.urlPath}/findByAttrIn`, {
            attrName, attrValues
        }));
        return resp as any as ModelType[];
    }

    // For UI tables (to implement table.query abstract method)
    find = async ( queryCriteria : TableViewQueryCriteria, populateChildren = false ) => {
        try {
            const result : any = await firstValueFrom(this.http.get(
                `${this.urlPath}/find`,
                { params: {populateChildren, criteria: JSON.stringify(queryCriteria)}}));
            const resp : TableViewQueryResponse<ModelType> = result.data
            return resp
        } catch (error) {
            console.error('find() error. query:', queryCriteria, 'error:', error?.message );
            throw error
        }
    }

    timestamp = (date: Date) => date?.toISOString()

    nowTimeStamp = () => this.timestamp(new Date())

    // override this to get rest endpoint specific error handling
    publishError(error: any, source: any, expireSecs = AppErrorMessageEvent.DEFAULT_EXPIRE_SECS): string {
        let errMsg = this.extractErrorMsg(error)
        this.appState.errorEmitter.emit(new AppErrorMessageEvent(errMsg, source, expireSecs))
        return errMsg
    }

    publishSuccess(msg: string, source: any, expireSecs = AppSuccessMessageEvent.DEFAULT_EXPIRE_SECS): string {
        this.appState.successEmitter.emit(new AppSuccessMessageEvent(msg, source, expireSecs))
        return msg
    }

    publishInfo(msg: string, source: any, expireSecs = AppInfoMessageEvent.DEFAULT_EXPIRE_SECS): string {
        this.appState.infoEmitter.emit(new AppInfoMessageEvent(msg, source, expireSecs))
        return msg
    }

    extractErrorMsg(error: any): string {
        return RestClient.extractErrorMessage(error)
    }

    static extractErrorMessage(error:any) {
        if (error instanceof Error) {
            return `${error.message}`
        } else if (error.error) {
            if (error.error instanceof ErrorEvent) {
                return `${error.error.message}`
            } else if ( error.error instanceof ArrayBuffer ) {
                try {
                    console.dir(error.error)
                    const decodedString = String.fromCharCode.apply(null, new Uint8Array(error.error));
                    console.error(decodedString)
                    //Example: {"error":{"type":"rest-api","reason":"This is an error."}}
                    const errorResp = JSON.parse(decodedString);
                    if ( errorResp?.error?.reason ) {
                        return errorResp.error.reason
                    }
                } catch ( error : any ) {
                    console.error(error)
                }
            }
            let rtnMsg = 'Error'
            const httpResp = error
            const respBodyObj = httpResp.error
            if (respBodyObj.error?.reason) {
                // json content from server that has 'error' element
                rtnMsg = respBodyObj.error.reason
            } else if ( respBodyObj.code && respBodyObj.msg != 'OK') {
                // json content from server that has 'code' element where 0 is success
                rtnMsg = respBodyObj.msg
                if ( !rtnMsg ) {
                    rtnMsg = 'Server returned error.'
                }
                if ( typeof(respBodyObj.data)=='string' && !rtnMsg || (''+rtnMsg).length < 100 ) {
                    if ( !rtnMsg.endsWith('.') ) {
                        rtnMsg += '.'
                    }
                    rtnMsg += '  ' + respBodyObj.data
                    if ( rtnMsg.length > 200 ) {
                        rtnMsg = rtnMsg.slice(0,200) + '...'
                    }
                }
                rtnMsg += ` (code=${respBodyObj.code})`
            } else if (respBodyObj instanceof ProgressEvent && respBodyObj.type == 'error') {
                // client side error
                rtnMsg = 'Network error occurred'
                if ( httpResp.url ) {
                    rtnMsg += ` (${httpResp.url})`
                }
            } else {
                rtnMsg = httpResp.statusText ? `${httpResp.statusText} (${error.url})` : "Unknown Error"
            }
            return rtnMsg
        } else {
            console.error(error)
            return `${error}`
        }
    }

}